import {useCallback, useMemo} from "react";
import {
    type InfiniteData,
    useMutation,
    useQuery,
    useQueryClient,
    useSuspenseInfiniteQuery
} from "@tanstack/react-query";
import {
    getUnreadCount,
    listNotifications,
    markAllNotificationsRead,
    markNotificationsRead,
    type NotificationItem,
    type NotificationPage,
    type NotificationSettingWrite,
    resolveNotificationSetting,
    type ResolvedNotificationSetting,
    setNotificationSetting
} from "@/data/notification-api";

export const notificationKeys = {
    // No organization id here, unlike chatKeys. The inbox is a USER artifact: a DM notification
    // belongs to the same list whichever org the thread lives in, and the screen sits at
    // /notifications rather than under /organization/[orgId]. Scoping it per org would split one
    // inbox into several and make the badge count depend on which org happened to be open.
    list: () => ["notifications", "list"] as const,
    unreadCount: () => ["notifications", "unreadCount"] as const,
    // One subtree for everything settings-shaped, so a write can invalidate all of it in one call.
    // It has to: the resolved value of a channel depends on the type-level row above it, so a
    // type-level write changes answers this app never asked about by name.
    settings: () => ["notifications", "settings"] as const,
    resolvedSetting: (type: string, discriminator: string) =>
        ["notifications", "settings", "resolved", type, discriminator] as const
};

/** The backend caps perPage at 100; 25 is its default and roughly two screens of rows. */
const PAGE_SIZE = 25;

/** The whole inbox, as much of it as has been paged in. */
export interface NotificationInbox {
    items: NotificationItem[];
    /** Unread across the ENTIRE inbox, not just the loaded pages — the server counts it. */
    unreadCount: number;
    hasMore: boolean;
    isLoadingMore: boolean;
    loadMore: () => void;
    isRefreshing: boolean;
    refresh: () => void;
}

/**
 * The notification list.
 *
 * An infinite query rather than a first-page-plus-refresh: the endpoint is genuinely paged, and a
 * chat-heavy user accumulates rows faster than one page holds. Cutting the list off at 25 with no
 * affordance would hide the rest with nothing on screen to say so.
 */
export function useNotificationInbox(): NotificationInbox {
    const query = useSuspenseInfiniteQuery({
        queryKey: notificationKeys.list(),
        queryFn: ({pageParam}) => listNotifications({page: pageParam, perPage: PAGE_SIZE}),
        initialPageParam: 1,
        getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
        staleTime: 1000 * 30
    });

    const items = useMemo(() => query.data.pages.flatMap((page) => page.items), [query.data.pages]);
    const {fetchNextPage, refetch} = query;

    return {
        items,
        // Every page carries the same inbox-wide total; the first is the one that always exists.
        unreadCount: query.data.pages[0].unreadCount,
        hasMore: query.hasNextPage,
        isLoadingMore: query.isFetchingNextPage,
        loadMore: useCallback(() => void fetchNextPage(), [fetchNextPage]),
        isRefreshing: query.isRefetching,
        refresh: useCallback(() => void refetch(), [refetch])
    };
}

/**
 * The unread total on its own, for the header bell and the OS app-icon badge.
 *
 * Deliberately `useQuery` and not the `useSuspenseQuery` the rest of this repo reads with: the
 * bell decorates the organization dashboard, so suspending on it would blank a screen the user
 * asked for, and throwing on it would send the dashboard to the error boundary because a
 * decoration failed to load.
 *
 * `undefined` means "not known yet", which is NOT the same as zero and is left to the caller to
 * interpret: the bell shows nothing, while the OS badge leaves alone whatever value the last push
 * put there rather than blanking it on every cold start.
 */
export function useUnreadNotificationCount(): number | undefined {
    const {data} = useQuery({
        queryKey: notificationKeys.unreadCount(),
        queryFn: getUnreadCount,
        staleTime: 1000 * 60
    });
    return data;
}

/**
 * Apply a completed read-write to the cache instead of refetching.
 *
 * Two reasons this is `setQueryData` where `data/chat-hooks.tsx` would invalidate. First, the
 * effect of the write is exactly known — these rows became read — and the authoritative unread
 * total came back on the response, so a refetch could only re-derive what we already hold.
 * Second, invalidating an INFINITE query refetches every page that has been loaded, and marking
 * read is the most frequent write on this screen: one tap would cost N requests and visibly churn
 * the list under the user's finger.
 */
function applyRead(
    queryClient: ReturnType<typeof useQueryClient>,
    isNowRead: (item: NotificationItem) => boolean,
    unreadCount: number
): void {
    queryClient.setQueryData<InfiniteData<NotificationPage, number>>(notificationKeys.list(), (current) =>
        current && {
            ...current,
            pages: current.pages.map((page) => ({
                ...page,
                unreadCount,
                items: page.items.map((item) => (item.read || !isNowRead(item) ? item : {...item, read: true}))
            }))
        }
    );
    queryClient.setQueryData(notificationKeys.unreadCount(), unreadCount);
}

/** Mark specific rows read — what a tap on a row does. */
export function useMarkNotificationsRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (ids: string[]) => markNotificationsRead(ids),
        onSuccess: (result, ids) => {
            const marked = new Set(ids);
            applyRead(queryClient, (item) => marked.has(item.id), result.unreadCount);
        }
    });
}

/** Empty the badge in one call, including rows that have not been paged in yet. */
export function useMarkAllNotificationsRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: markAllNotificationsRead,
        onSuccess: (result) => {
            applyRead(queryClient, () => true, result.unreadCount);
        }
    });
}

/**
 * What one (type, discriminator) actually resolves to, straight from the backend.
 *
 * `undefined` while unknown, and — as with the unread count — that is NOT `false`. A mute control
 * that renders "on" before the answer arrives would lie to a user who has muted, and one that
 * renders "off" would lie to everyone else, so callers are given the third state and decide.
 *
 * `useQuery`, not the `useSuspenseQuery` most of this repo reads with, for the same reason the
 * unread count is: both callers are decorations on a screen the user asked for something else
 * from — a card on the plan screen and an icon in the chat header — and neither should be able to
 * blank that screen or send it to the error boundary by failing to load.
 */
export function useResolvedNotificationSetting(type: string, discriminator = ""): boolean | undefined {
    const {data} = useQuery({
        queryKey: notificationKeys.resolvedSetting(type, discriminator),
        queryFn: () => resolveNotificationSetting(type, discriminator),
        staleTime: 1000 * 60
    });
    return data?.enabled;
}

/**
 * Write a setting and bring the cache back in line with the backend.
 *
 * `invalidateQueries` over the whole settings subtree, deliberately, and NOT the `setQueryData`
 * that `applyRead` above uses. Two differences, both of which point the other way here. First, the
 * effect of this write is not local: turning the chat type off changes what EVERY channel resolves
 * to, and the backend additionally sweeps rows that the write made redundant — so a cached answer
 * for a channel this mutation never mentioned can be wrong afterwards, and only the server knows
 * which. Second, none of these are infinite queries, so invalidating costs one small request per
 * mounted resolve rather than a page-by-page refetch.
 *
 * The exact key written is still seeded from the response first, so the switch the user just
 * flipped settles immediately on the RESOLVED value rather than flickering through a refetch.
 */
export function useSetNotificationSetting() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (write: NotificationSettingWrite) => setNotificationSetting(write),
        onSuccess: (result, write) => {
            const discriminator = write.discriminator ?? "";
            queryClient.setQueryData<ResolvedNotificationSetting>(
                notificationKeys.resolvedSetting(write.type, discriminator),
                {type: write.type, discriminator, enabled: result.enabled}
            );
            void queryClient.invalidateQueries({queryKey: notificationKeys.settings()});
        }
    });
}
